import { useMemo } from 'react';
import { Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpLabel } from '@/features/guided-help';
import { cn } from '@/lib/utils';
import { DateInputWithCalendar } from '@/components/ui/date-input-with-calendar';
import {
  TimeUnitInput,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
} from '@/components/ui/time-unit-input';
import { Label } from '@/components/ui/label';
import type { TournamentConfigFieldConfig } from './types';
import { DEFAULT_TOURNAMENT_CONFIG_FIELD_CONFIG } from './types';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';

/** Hour the deadline falls back to when the manager only picks a day. */
const DEFAULT_SIGNUP_DEADLINE_HOUR = 23;
const DEFAULT_SIGNUP_DEADLINE_MINUTE = 59;

const pad = (n: number) => String(n).padStart(2, '0');

export interface SignupDeadlineFieldProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  config?: TournamentConfigFieldConfig;
  wrapInCard?: boolean;
  inputId?: string;
  /** When set, overrides the default minimum (today + `minDaysFromNow`). */
  calendarMinDate?: Date;
  /** When set, disables dates after this calendar day (inclusive). */
  calendarMaxDate?: Date;
  /** Renders the field read-only. */
  disabled?: boolean;
}

export function SignupDeadlineField({
  value,
  onChange,
  config: userConfig,
  wrapInCard = true,
  inputId = 'signup-deadline',
  calendarMinDate,
  calendarMaxDate,
  disabled = false,
}: SignupDeadlineFieldProps) {
  const config = { ...DEFAULT_TOURNAMENT_CONFIG_FIELD_CONFIG, ...userConfig };

  const minDate = useMemo(() => {
    if (calendarMinDate) {
      return calendarMinDate;
    }
    const d = new Date();
    d.setDate(
      d.getDate() + (config.signupDeadline?.minDaysFromNow ?? 1),
    );
    return d;
  }, [calendarMinDate, config.signupDeadline?.minDaysFromNow]);

  // The deadline is a single Date: the calendar owns the day and these two own the time, so the
  // hour survives a day change and vice versa.
  const hour = value ? pad(value.getHours()) : '';
  const minute = value ? pad(value.getMinutes()) : '';

  // A day with no time yet closes at 23:59, which is what every deadline meant before the hour
  // became editable.
  const handleDateChange = (date: Date | null) => {
    if (!date) {
      onChange(null);
      return;
    }
    const next = new Date(date);
    next.setHours(
      value?.getHours() ?? DEFAULT_SIGNUP_DEADLINE_HOUR,
      value?.getMinutes() ?? DEFAULT_SIGNUP_DEADLINE_MINUTE,
      0,
      0,
    );
    onChange(next);
  };

  // Half-typed input ("2" on its way to "20") leaves the Date alone until it is a real number.
  const handleTimeChange = (unit: 'hours' | 'minutes', raw: string) => {
    if (!value || raw === '') return;
    const next = new Date(value);
    if (unit === 'hours') {
      next.setHours(Number(raw));
    } else {
      next.setMinutes(Number(raw));
    }
    next.setSeconds(0, 0);
    onChange(next);
  };

  const field = (
    <div className="space-y-3" data-guide-id="wizard.config.signupDeadline">
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-3">
          <HelpLabel htmlFor={inputId} topicId="wizard.config.signupDeadline">
            Fecha límite para inscribirse *
          </HelpLabel>
          <DateInputWithCalendar
            id={inputId}
            value={value}
            onChange={handleDateChange}
            minDate={minDate}
            maxDate={calendarMaxDate}
            disabled={disabled}
            className={cn('max-w-xs', !value && 'text-gray-400')}
            placeholder="dd/mm/aaaa"
          />
        </div>

        <div className="space-y-3">
          <Label htmlFor={`${inputId}-hour`}>Hora *</Label>
          <div className="flex items-center gap-2">
            <TimeUnitInput
              id={`${inputId}-hour`}
              value={hour}
              onChange={(v) => handleTimeChange('hours', v)}
              options={HOUR_OPTIONS}
              maxValue={23}
              placeholder="Hora"
              ariaLabel="Hora límite de inscripción"
              disabled={disabled || !value}
            />
            <span className="select-none text-muted-foreground" aria-hidden>
              :
            </span>
            <TimeUnitInput
              id={`${inputId}-minute`}
              value={minute}
              onChange={(v) => handleTimeChange('minutes', v)}
              options={MINUTE_OPTIONS}
              maxValue={59}
              placeholder="Min"
              ariaLabel="Minutos del límite de inscripción"
              disabled={disabled || !value}
            />
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-500">
        Los jugadores podrán inscribirse hasta las{' '}
        {value ? `${hour}:${minute}` : '23:59'} de esa fecha, en la hora del
        club. Después, el torneo pasará a estado de inscripción cerrada.
      </p>
    </div>
  );

  if (!wrapInCard) {
    return <div className={wizardBlockShellClassName}>{field}</div>;
  }

  return (
    <Card className={wizardBlockCardClassName}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="h-5 w-5 text-blue-600" />
          Cierre de inscripción
        </CardTitle>
      </CardHeader>
      <CardContent>{field}</CardContent>
    </Card>
  );
}
